import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Part } from '../lib/api'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { Badge } from './ui/badge'
import { cn } from '../lib/utils'

// Rendering a message means rendering its parts, in order, each in the idiom
// the plan assigns it: text is a bubble, reasoning is a collapsible "thought",
// a tool call and its result collapse into one activity chip, an error is
// visible rather than swallowed.
//
// The load-bearing rule is that nothing is rendered as debug output. A user who
// never opens the reasoning block or the tool chip should still read a clean
// answer; a user who opens them should find something legible rather than a
// JSON dump.

/** Markdown renders agent prose. */
function Markdown({ children }: { children: string }) {
  return (
    <div
      className={cn(
        '[&_p]:mb-3 [&_p:last-child]:mb-0',
        // ul/li/ol markers back on: a plain <ul>/<ol> renders unstyled by
        // default without an explicit list-style, which turns every
        // agent-written bulleted answer into an indented run-on.
        '[&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-6',
        '[&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6',
        '[&_li]:mb-1',
        '[&_pre]:mb-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:text-[0.85em]',
        '[&_code]:font-mono [&_code]:text-[0.9em]',
        '[&_table]:mb-3 [&_table]:w-full [&_table]:border-collapse',
        '[&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
        '[&_td]:border [&_td]:px-2 [&_td]:py-1 [&_td]:text-left',
        '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:opacity-85',
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 className="mb-2 text-lg font-semibold">{children}</h1>,
          h2: ({ children }) => <h2 className="mb-2 text-base font-semibold">{children}</h2>,
          h3: ({ children }) => <h3 className="mb-2 text-sm font-semibold">{children}</h3>,
          a: ({ href, children }) => (
            // Agent output is untrusted text; noreferrer/noopener keeps a link
            // in a reply from reaching back into this tab.
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">
              {children}
            </a>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}

/** Disclosure is the shared shell for the two collapsible affordances. */
function Disclosure({
  label,
  tone = 'text-muted-foreground',
  defaultOpen = false,
  children,
  'data-testid': testId,
}: {
  label: string
  tone?: string
  defaultOpen?: boolean
  children: React.ReactNode
  'data-testid'?: string
}) {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="group my-2 rounded-md border bg-muted/40"
      data-testid={testId}
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-left">
        <span className={cn('text-xs font-medium', tone)}>
          <span className="hidden group-data-[state=open]:inline">▾</span>
          <span className="inline group-data-[state=open]:hidden">▸</span> {label}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 text-sm text-muted-foreground">{children}</CollapsibleContent>
    </Collapsible>
  )
}

/** prettyJSON renders tool arguments and results without a wall of escaping. */
function prettyJSON(value: unknown, fallback: string): string {
  if (value === undefined || value === null) return fallback
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return fallback
  }
}

function Pre({ children }: { children: string }) {
  return (
    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-sm bg-muted p-2 font-mono text-xs">
      {children}
    </pre>
  )
}

/** PartView renders one part according to its kind. */
export function PartView({ part, result }: { part: Part; result?: Part }) {
  switch (part.kind) {
    case 'text':
      return (
        <div data-testid="part-text">
          <Markdown>{part.text ?? ''}</Markdown>
          {part.status === 'streaming' && (
            <span
              className="ml-1 inline-block h-[1em] w-2 align-text-bottom bg-muted-foreground"
              style={{ animation: 'parley-blink 1s steps(2, start) infinite' }}
            />
          )}
        </div>
      )

    case 'reasoning':
      return (
        <Disclosure
          label={part.status === 'streaming' ? 'Thinking…' : 'Thought about this'}
          data-testid="part-reasoning"
        >
          <p className="whitespace-pre-wrap">{part.text}</p>
        </Disclosure>
      )

    case 'tool_call': {
      // The result is folded into the call rather than shown as its own block:
      // "ran get_time and got X" is one event to a reader, even though it is
      // two chunks on the wire.
      const failed = result?.status === 'error'
      const label = result
        ? `${failed ? 'Failed' : 'Ran'} ${part.name ?? 'tool'}`
        : part.status === 'streaming'
          ? `Calling ${part.name ?? 'tool'}…`
          : `Called ${part.name ?? 'tool'}`
      return (
        <Disclosure label={label} tone={failed ? 'text-destructive' : 'text-muted-foreground'} data-testid="part-tool">
          <p className="text-xs font-medium">Arguments</p>
          <Pre>{prettyJSON(part.data ?? part.text, '{}')}</Pre>
          {result && (
            <>
              <p className="mt-3 text-xs font-medium">{failed ? 'Error' : 'Result'}</p>
              <Pre>{prettyJSON(result.data ?? result.text, '(no output)')}</Pre>
            </>
          )}
        </Disclosure>
      )
    }

    // A tool result is always rendered inside its call above. Reaching here
    // means the call never arrived — a malformed stream — and showing the
    // orphan is better than silently dropping evidence of what ran.
    case 'tool_result':
      return (
        <Disclosure label={`Result from ${part.name ?? 'a tool'}`}>
          <Pre>{prettyJSON(part.data ?? part.text, '(no output)')}</Pre>
        </Disclosure>
      )

    case 'file': {
      const data = (part.data ?? {}) as { url?: string; filename?: string; mimeType?: string }
      const name = part.name ?? data.filename ?? 'attachment'
      return (
        <div className="my-2" data-testid="part-file">
          {data.url && data.mimeType?.startsWith('image/') ? (
            <img src={data.url} alt={name} className="max-w-full rounded-md" />
          ) : (
            <Badge variant="secondary">📎 {name}</Badge>
          )}
        </div>
      )
    }

    case 'source': {
      const data = (part.data ?? {}) as { url?: string; title?: string }
      return (
        <p className="my-1 text-xs text-muted-foreground">
          Source:{' '}
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
            {part.name ?? data.title ?? data.url}
          </a>
        </p>
      )
    }

    case 'task':
      return (
        <Disclosure label={`Background task: ${part.name ?? 'working…'}`}>
          <Pre>{prettyJSON(part.data, '{}')}</Pre>
        </Disclosure>
      )

    case 'error':
      return (
        <div
          className="my-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="part-error"
        >
          {part.text || 'The agent reported an error.'}
        </div>
      )

    default:
      // An unknown part kind is a chunk type this build has never heard of.
      // Showing it as a small code block keeps the conversation honest instead
      // of pretending nothing happened.
      return <code className="text-xs">{part.kind}</code>
  }
}

/** PartsView renders a message's parts, pairing tool results onto their calls. */
export function PartsView({ parts }: { parts: Part[] }) {
  const resultByCallID = new Map<string, Part>()
  for (const p of parts) {
    if (p.kind === 'tool_result' && p.tool_call_id) resultByCallID.set(p.tool_call_id, p)
  }

  return (
    <>
      {parts
        .filter((p) => !(p.kind === 'tool_result' && p.tool_call_id && resultByCallID.has(p.tool_call_id)))
        .sort((a, b) => a.index - b.index)
        .map((p) => (
          <PartView
            key={p.index}
            part={p}
            result={p.kind === 'tool_call' && p.tool_call_id ? resultByCallID.get(p.tool_call_id) : undefined}
          />
        ))}
    </>
  )
}
